/**
 * The agent builder (docs/superpowers/specs/2026-10-27-agent-builder-design.md): a build is a
 * Claude agent working for 10–30 minutes in a sandbox, so this starts one and then polls it.
 * Its own origin (a Lambda Function URL) — base 'agent'.
 */
import { request } from './client';

export interface AgentImage {
  name: string;
  contentType: string;
  /** base64, no data: prefix */
  data: string;
}

export interface ProgressLine {
  id: string;
  at?: string;
  text: string;
  kind: 'note' | 'step' | 'check';
}

export type AgentBuildStatus = 'running' | 'finalizing' | 'live' | 'failed';

export interface AgentBuild {
  buildId: string;
  siteId: string;
  kind: 'create' | 'change';
  status: AgentBuildStatus;
  progress: ProgressLine[];
  url?: string;
  title?: string;
  summary?: string;
  error?: string;
  startedAt: string;
  finishedAt?: string;
}

export interface AgentSite {
  siteId: string;
  slug?: string | null;
  title?: string | null;
  kind?: string | null;
  updatedAt: string;
  currentBuildId?: string;
  pendingBuildId?: string;
  description?: string;
}

export function agentConfigured(): boolean {
  return Boolean(process.env.EXPO_PUBLIC_AGENT_BUILD_URL);
}

export function startAgentBuild(prompt: string, images: AgentImage[]): Promise<{ buildId: string; siteId: string }> {
  return request('/agent/builds', { method: 'POST', base: 'agent', body: { prompt, images }, timeoutMs: 90_000 });
}

export function getAgentBuild(buildId: string): Promise<AgentBuild> {
  // Publishing happens inside the poll that first sees the build finished, which can take a while.
  return request(`/agent/builds/${buildId}`, { base: 'agent', timeoutMs: 120_000 });
}

export function startAgentChange(siteId: string, message: string, images: AgentImage[]): Promise<{ buildId: string }> {
  return request(`/agent/sites/${siteId}/changes`, { method: 'POST', base: 'agent', body: { message, images }, timeoutMs: 90_000 });
}

/** What is left of this month's agent builds (owner, 2026-11-03: Monthly includes 5 new sites a month). */
export interface AgentAllowance {
  unlimited: boolean;
  sitesLeft: number;
  sitesPerMonth: number;
  changesLeft: number;
  /** yyyy-mm-dd, the first of next month (UTC). */
  resetsOn: string;
}

export async function listAgentSites(): Promise<AgentSite[]> {
  return (await loadStudio()).sites;
}

/** The studio's front page: the caller's sites, newest first, and their allowance. */
export async function loadStudio(): Promise<{ sites: AgentSite[]; allowance: AgentAllowance | null }> {
  const res = await request<{ sites: AgentSite[]; allowance?: AgentAllowance }>('/agent/sites', { base: 'agent' });
  const sites = [...(res.sites ?? [])].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
  return { sites, allowance: res.allowance ?? null };
}

export function getAgentSite(siteId: string): Promise<AgentSite> {
  return request(`/agent/sites/${siteId}`, { base: 'agent' });
}

/** The live address of a site. */
export function siteUrl(slug: string): string {
  return `https://${slug}.saddle-sites.com`;
}

/** The phone screenshot the agent took of a build (public, immutable). */
export function buildShotUrl(slug: string, buildId: string): string {
  return `${siteUrl(slug)}/_b/${buildId}/_saddle/phone.png`;
}

const MAX_BYTES = 3.5 * 1024 * 1024;
const MAX_SIDE = 2400;

/**
 * A picked file, ready to send. Screenshots stay PNG when they fit (cloning needs exact
 * colours); anything else — iPhone HEIC photos especially, which the server cannot read — is
 * redrawn as JPEG, no larger than 2400px on its long side.
 */
export async function prepareImage(file: File): Promise<AgentImage> {
  const type = file.type.toLowerCase();
  if ((type === 'image/png' || type === 'image/jpeg' || type === 'image/webp') && file.size <= MAX_BYTES) {
    return { name: file.name || 'image', contentType: type, data: await toBase64(file) };
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not read that image'))), 'image/jpeg', 0.9),
  );
  const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
  return { name, contentType: 'image/jpeg', data: await toBase64(blob) };
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read that image'));
    reader.readAsDataURL(blob);
  });
}

export interface PublicSite {
  slug: string;
  title: string;
  description?: string;
  shotUrl: string;
  remixable: boolean;
}

/** What a "make your own" page shows about the site it came from (no sign-in needed). */
export function getPublicSite(slug: string): Promise<PublicSite> {
  return request(`/agent/public/${slug}`, { base: 'agent' });
}

/** Your own version of someone else's site, starting from a copy of it. */
export function startRemix(slug: string, message: string, images: AgentImage[]): Promise<{ buildId: string; siteId: string }> {
  return request('/agent/remix', { method: 'POST', base: 'agent', body: { slug, message, images }, timeoutMs: 90_000 });
}
